import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";
import { useHostLogs } from "../hooks/useHostLogs";
import StatusBadge from "../components/StatusBadge";
import { LogPanel } from "../components/LogPanel";
import HostTopology from "../components/HostTopology";
import type { ApiTopologyHub, MatchConfidence } from "../lib/topologyAdapter";

interface Device {
  id: string;
  serial: string;
  model: string;
  platform: string;
  os_version: string;
  status: string;
  battery_level: number | null;
  has_network: boolean;
  failed_attempts: number;
  adb_tcp_port: number | null;
  // USB topology fields (GET /hosts/:id, see src/api/hosts.ts) — hub_id/
  // link_speed are always present (null when unresolved); match_confidence
  // is not currently shipped by the API, so it's optional here too.
  hub_id: string | null;
  link_speed: string | null;
  match_confidence?: MatchConfidence;
}

interface HostInfo {
  id: string;
  hostname: string;
  ip_address: string;
  ssh_port: number;
  max_devices: number;
  status: string;
  runner_tag: string | null;
  last_heartbeat_at: string | null;
  created_at: string;
}

interface HostDetailResponse {
  host: HostInfo;
  devices: Device[];
  hubs: ApiTopologyHub[];
}

export default function HostDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi<HostDetailResponse>(`/api/hosts/${id}`);
  useSSERefetch("/api/events", ["host:heartbeat", "device:status", "device:claimed", "device:released"], refetch);
  // Separate, debounced subscription for USB topology changes — hub health
  // flips can arrive in quick bursts (a whole hub's devices going offline
  // together), so this coalesces them into one refetch instead of one per
  // event.
  useSSERefetch("/api/events", ["host:topology"], refetch, 300);
  const { lines: logLines, live: logsLive } = useHostLogs(id);

  if (loading && !data) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading host...</p>;
  }

  if (error || !data) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        {error ?? "Host not found."}
      </p>
    );
  }

  const { host, devices } = data;

  return (
    <div>
      <nav style={{ marginBottom: "var(--space-lg)", fontSize: "var(--font-size-sm)" }}>
        <Link to="/hosts" style={{ color: "var(--color-info)" }}>Hosts</Link>
        <span style={{ margin: "0 var(--space-xs)", color: "var(--color-text-secondary)" }}>/</span>
        <span>{host.hostname}</span>
      </nav>

      {/* Host info */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "var(--space-lg)",
        marginBottom: "var(--space-xl)",
      }}>
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Host Info</h2>
          <Row label="Hostname" value={host.hostname} />
          <Row label="IP Address" value={host.ip_address} mono />
          <Row label="SSH Port" value={String(host.ssh_port)} />
          <Row label="Runner Tag" value={host.runner_tag ?? "—"} mono />
          <Row label="Max Devices" value={String(host.max_devices)} />
          <div style={rowStyle}>
            <span style={labelStyle}>Status</span>
            <StatusBadge status={host.status} />
          </div>
          <Row
            label="Last Heartbeat"
            value={host.last_heartbeat_at ? new Date(host.last_heartbeat_at).toLocaleString() : "Never"}
          />
        </div>

        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Device Summary</h2>
          <Row label="Total" value={String(devices.length)} />
          <Row label="Available" value={String(devices.filter(d => d.status === "available").length)} />
          <Row label="Busy" value={String(devices.filter(d => d.status === "busy").length)} />
          <Row label="Offline" value={String(devices.filter(d => d.status === "offline").length)} />
        </div>
      </div>

      {/* USB Topology & Health — host -> hubs -> devices fan-out, hub health
          cards, and a live connection stability strip. NO power KPI/meter —
          this hardware has no live power telemetry (see hub-health.ts). */}
      <HostTopology
        hostname={host.hostname}
        ipAddress={host.ip_address}
        hubs={data.hubs ?? []}
        devices={devices}
      />

      {/* Device list */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Devices ({devices.length})</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--color-foreground)" }}>
              {["Model", "Serial", "OS", "ADB Connect", "Status", "Battery", "Network"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {devices.map(device => (
              <tr
                key={device.id}
                onClick={() => navigate(`/devices/${device.id}`)}
                style={{ cursor: "pointer", borderBottom: "var(--border-default)" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--color-foreground)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                <td style={tdStyle}>{device.model}</td>
                <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: "var(--font-size-sm)" }}>
                  {device.serial}
                </td>
                <td style={tdStyle}>Android {device.os_version}</td>
                <td style={{ ...tdStyle, fontFamily: "var(--font-mono)", fontSize: "var(--font-size-sm)" }}>
                  {device.adb_tcp_port ? `${host.ip_address}:${device.adb_tcp_port}` : "—"}
                </td>
                <td style={tdStyle}><StatusBadge status={device.status} /></td>
                <td style={tdStyle}>
                  {device.battery_level != null ? `${device.battery_level}%` : "—"}
                </td>
                <td style={tdStyle}>
                  <span style={{ color: device.has_network ? "var(--color-success)" : "var(--color-danger)" }}>
                    {device.has_network ? "OK" : "No"}
                  </span>
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "var(--color-text-secondary)" }}>
                  No devices connected to this host.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Agent log feed — fed by the heartbeat ring buffer + live SSE */}
      <div style={{ marginTop: "var(--space-xl)" }}>
        <LogPanel
          title="Recent agent events"
          lines={logLines}
          live={logsLive}
          emptyHint="No agent log lines yet — the agent ships its recent stdout on each heartbeat (~15s)."
          height={360}
        />
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={{ fontSize: "var(--font-size-base)", fontFamily: mono ? "var(--font-mono)" : "inherit" }}>
        {value}
      </span>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-card)",
  padding: "var(--space-lg)",
};
const cardTitleStyle: React.CSSProperties = {
  fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--color-primary)", marginBottom: "var(--space-md)",
};
const rowStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", padding: "var(--space-xs) 0",
};
const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px",
};
const thStyle: React.CSSProperties = {
  textAlign: "left", padding: "var(--space-sm) var(--space-md)", fontSize: "var(--font-size-sm)", fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.5px",
};
const tdStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-md)", fontSize: "var(--font-size-base)", verticalAlign: "middle",
};
